import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { validate2FAForLogin, checkLoginAttempts, recordFailedAttempt } from '../api/authApi';
import { toast } from 'react-toastify';
import { LogIn, Shield, AlertTriangle } from 'lucide-react';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [tempUserId, setTempUserId] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);
  const [failed2FAAttempts, setFailed2FAAttempts] = useState(0);

  const { login, isAuthenticated, error } = useContext(AuthContext);
  const navigate = useNavigate();

  // Engelleme durumunu kontrol et
  useEffect(() => {
    const checkBlockStatus = async () => {
      try {
        const blockStatus = await checkLoginAttempts();
        
        if (blockStatus.isBlocked) {
          setIsBlocked(true);
          setBlockedUntil(new Date(blockStatus.blockedUntil));
          
          // Kalan süreyi hesapla
          const now = new Date();
          const timeLeft = Math.max(0, Math.floor((new Date(blockStatus.blockedUntil) - now) / 1000));
          setRemainingTime(timeLeft);
        }
      } catch (err) {
        console.error("Engelleme durumu kontrol edilirken hata:", err);
      }
    };
    
    checkBlockStatus();
  }, []);

  // Kalan süreyi güncelle
  useEffect(() => {
    if (!isBlocked || !blockedUntil) return;
    
    const timer = setInterval(() => {
      const now = new Date();
      const timeLeft = Math.max(0, Math.floor((blockedUntil - now) / 1000));
      
      setRemainingTime(timeLeft);
      
      if (timeLeft <= 0) {
        setIsBlocked(false);
        setBlockedUntil(null);
        clearInterval(timer);
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isBlocked, blockedUntil]);

  // Kullanıcı zaten giriş yapmışsa yönlendir
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Hata durumunda bildirim göster
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isBlocked) {
      toast.error('Çok fazla başarısız giriş denemesi. Lütfen daha sonra tekrar deneyin.');
      return;
    }
    
    if (!username || !password) {
      toast.error('Lütfen kullanıcı adı ve şifrenizi girin');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const userData = await login(username, password);
      
      // Kullanıcının 2FA'sı etkin mi kontrol et
      if (userData && userData.is_2fa_enabled) {
        // 2FA doğrulama adımını göster
        setShow2FA(true);
        setTempUserId(userData.id);
        toast.info('Lütfen iki faktörlü kimlik doğrulama kodunu girin');
      } else {
        // 2FA etkin değilse direkt giriş yap
        toast.success('Giriş başarılı');
        navigate('/dashboard');
      }
    } catch (err) {
      // Başarısız giriş denemesini kaydet
      try {
        await recordFailedAttempt(username);
        const blockStatus = await checkLoginAttempts();
        
        if (blockStatus.isBlocked) {
          setIsBlocked(true);
          setBlockedUntil(new Date(blockStatus.blockedUntil));
          
          // Kalan süreyi hesapla
          const now = new Date();
          const timeLeft = Math.max(0, Math.floor((new Date(blockStatus.blockedUntil) - now) / 1000));
          setRemainingTime(timeLeft);
          
          toast.error(`Çok fazla başarısız giriş denemesi. Hesabınız ${formatRemainingTime(timeLeft)} süreyle engellendi.`);
        } else if (blockStatus.attempts >= 5) {
          toast.warning(`Dikkat: ${10 - blockStatus.attempts} başarısız deneme hakkınız kaldı. Ardından hesabınız 15 dakika engellenecek.`);
        }
      } catch (recordErr) {
        console.error("Başarısız giriş kaydedilirken hata:", recordErr);
      }
      
      // Hata AuthContext tarafından işleniyor
      console.error('Giriş hatası:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    
    if (isBlocked) {
      toast.error('Çok fazla başarısız giriş denemesi. Lütfen daha sonra tekrar deneyin.');
      return;
    }
    
    if (!verificationCode) {
      toast.error('Lütfen doğrulama kodunu girin');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // 2FA kodunu doğrula
      const userData = await validate2FAForLogin(verificationCode, tempUserId);
      
      // Yanıt içinde token olduğunu doğrula
      if (!userData || !userData.token) {
        throw new Error('Oturum açma token alınamadı. Lütfen tekrar giriş yapın.');
      }
      
      // Kullanıcıyı otomatik olarak oturum açtır
      localStorage.setItem('token', userData.token);
      toast.success('İki faktörlü doğrulama başarılı');
      window.location.href = '/dashboard'; // Sayfayı yeniden yükle (context güncellenmesi için)
    } catch (err) {
      console.error('2FA doğrulama hatası:', err);
      
      // Başarısız 2FA denemesini kaydet
      try {
        await recordFailedAttempt(username);
        const blockStatus = await checkLoginAttempts();
        
        if (blockStatus.isBlocked) {
          setIsBlocked(true);
          setBlockedUntil(new Date(blockStatus.blockedUntil));
          
          // Kalan süreyi hesapla
          const now = new Date();
          const timeLeft = Math.max(0, Math.floor((new Date(blockStatus.blockedUntil) - now) / 1000));
          setRemainingTime(timeLeft);
          
          toast.error(`Çok fazla başarısız giriş denemesi. Hesabınız ${formatRemainingTime(timeLeft)} süreyle engellendi.`);
        }
        
        // 2FA başarısız deneme sayısını artır
        const newFailedAttempts = failed2FAAttempts + 1;
        setFailed2FAAttempts(newFailedAttempts);
        
        // 3 başarısız 2FA denemesi sonrası giriş sayfasına geri dön
        if (newFailedAttempts >= 3) {
          setShow2FA(false);
          setTempUserId(null);
          setVerificationCode('');
          setFailed2FAAttempts(0);
          toast.error('Üç kez yanlış doğrulama kodu girdiniz. Lütfen tekrar giriş yapın.');
        } else {
          toast.error(`Yanlış doğrulama kodu. ${3 - newFailedAttempts} deneme hakkınız kaldı.`);
        }
      } catch (recordErr) {
        console.error("Başarısız 2FA denemesi kaydedilirken hata:", recordErr);
      }
      
      toast.error(err.message || 'Doğrulama kodu geçersiz');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Kalan süreyi formatla
  const formatRemainingTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-lg shadow-lg">
        <div>
          <div className="flex justify-center">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-20 w-20 text-primary-600" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            İade Kontrol
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {isBlocked
              ? 'Hesabınız geçici olarak engellendi'
              : show2FA 
                ? 'İki faktörlü kimlik doğrulama kodu gerekli' 
                : 'Lütfen giriş bilgilerinizi kullanarak sisteme erişin'}
          </p>
        </div>
        
        {isBlocked ? (
          <div className="mt-8 space-y-6">
            <div className="flex flex-col items-center">
              <AlertTriangle className="h-16 w-16 text-red-500 mb-4" />
              <h3 className="text-xl font-bold text-red-600 mb-2">Erişim Engellendi</h3>
              <p className="text-gray-700 text-center mb-4">
                Çok fazla başarısız giriş denemesi nedeniyle hesabınıza erişim geçici olarak engellendi.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 w-full">
                <p className="text-gray-700 text-center">
                  Kalan engelleme süresi:
                </p>
                <p className="text-2xl font-bold text-red-600 text-center">
                  {formatRemainingTime(remainingTime)}
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Lütfen belirtilen süre sonra tekrar deneyiniz veya yöneticinizle iletişime geçiniz.
              </p>
            </div>
          </div>
        ) : show2FA ? (
          <form className="mt-8 space-y-6" onSubmit={handle2FASubmit}>
            <div className="flex justify-center mb-4">
              <Shield className="h-12 w-12 text-primary-600" />
            </div>
            
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="verificationCode" className="sr-only">Doğrulama Kodu</label>
                <input
                  id="verificationCode"
                  name="verificationCode"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  maxLength={6}
                  autoComplete="off"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="6 haneli doğrulama kodu"
                />
              </div>
            </div>
            
            <p className="text-sm text-center text-gray-500 mb-4">
              Google Authenticator uygulamanızdaki 6 haneli kodu girin
            </p>

            <div>
              <button
                type="submit"
                disabled={isLoading || verificationCode.length !== 6}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Doğrulanıyor...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Shield className="h-4 w-4 mr-2" />
                    Doğrula ve Giriş Yap
                  </span>
                )}
              </button>
            </div>
            
            <div className="text-center">
              <button 
                type="button" 
                onClick={() => setShow2FA(false)}
                className="text-sm text-primary-600 hover:text-primary-800 font-medium"
              >
                Kullanıcı girişine dön
              </button>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="username" className="sr-only">Kullanıcı Adı</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Kullanıcı Adı"
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">Şifre</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Şifre"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Giriş yapılıyor...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <LogIn className="h-4 w-4 mr-2" />
                    Giriş Yap
                  </span>
                )}
              </button>
            </div>
            
            <div className="text-center text-sm text-gray-500">
              <p className="font-semibold mt-2 mb-1">Hesap bilgileri için spv ile iletişime geçiniz.</p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;